import type { SupabaseClient } from '@supabase/supabase-js';
import { useRouter } from '@tanstack/react-router';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { supabaseClient as SUPABASE_CLIENT } from '~/integrations/supabase/base/client.ts';
import type { PublicAuthSession } from '~/modules/auth/types.ts';
import { useInterval } from '~/utils/hooks.ts';
import { isBrowser } from '~/utils/is-browser.tsx';

const SupabaseContext = createContext<{
	accessToken: string | undefined;
	supabaseClient: SupabaseClient | undefined;
}>({
	accessToken: undefined,
	supabaseClient: undefined,
});

const SESSION_REFRESH_THRESHOLD_MS = 10 * 60 * 1_000;

export function StartSupabaseProvider({
	authSession,
	children,
}: Readonly<{
	authSession: PublicAuthSession | null;
	children: ReactNode;
}>) {
	const router = useRouter();
	const [realtimeSession, setRealtimeSession] = useState<PublicAuthSession | null>(authSession);
	const accessToken = realtimeSession?.accessToken;
	const expiresAt = realtimeSession?.expiresAt;
	const expiresIn = realtimeSession?.expiresIn;
	const [browserSessionExpiresAt, setBrowserSessionExpiresAt] = useState<number | undefined>();
	const [supabaseClient, setSupabaseClient] = useState<SupabaseClient | undefined>(() => {
		if (isBrowser) {
			return SUPABASE_CLIENT;
		}
	});

	const refreshBrowserSession = useCallback(async () => {
		if (!expiresIn) {
			return;
		}

		try {
			const response = await fetch('/auth/refresh-session', {
				method: 'POST',
			});

			if (!response.ok) {
				return;
			}

			const nextSession = (await response.json()) as PublicAuthSession | null;
			if (!nextSession?.accessToken) {
				return;
			}

			setRealtimeSession((previousSession) => {
				if (
					previousSession?.accessToken === nextSession.accessToken &&
					previousSession?.expiresAt === nextSession.expiresAt &&
					previousSession?.expiresIn === nextSession.expiresIn
				) {
					return previousSession;
				}

				return nextSession;
			});
		} catch (error) {
			console.error('[SupabaseProvider] Failed to refresh browser auth session', error);
		}
	}, [expiresIn]);

	useInterval(() => {
		void refreshBrowserSession();
	}, expiresIn);

	useEffect(() => {
		if (!accessToken || !expiresAt) {
			return;
		}

		if (expiresAt * 1_000 - Date.now() > SESSION_REFRESH_THRESHOLD_MS) {
			return;
		}

		void refreshBrowserSession();
	}, [accessToken, expiresAt, refreshBrowserSession]);

	useEffect(() => {
		if (!isBrowser || !authSession?.accessToken || authSession.expiresAt === browserSessionExpiresAt) {
			return;
		}

		setRealtimeSession((previousSession) => {
			if (
				previousSession?.accessToken === authSession.accessToken &&
				previousSession?.expiresAt === authSession.expiresAt &&
				previousSession?.expiresIn === authSession.expiresIn
			) {
				return previousSession;
			}

			return authSession;
		});
		setSupabaseClient(SUPABASE_CLIENT);
		setBrowserSessionExpiresAt(authSession.expiresAt);
	}, [authSession, browserSessionExpiresAt]);

	useEffect(() => {
		const shellIsAuthenticated = Boolean(authSession?.accessToken);
		const realtimeIsAuthenticated = Boolean(accessToken);

		if (shellIsAuthenticated === realtimeIsAuthenticated) {
			return;
		}

		void router.invalidate();
	}, [accessToken, authSession?.accessToken, router]);

	const value = useMemo(
		() => ({
			accessToken,
			supabaseClient,
		}),
		[accessToken, supabaseClient],
	);

	return <SupabaseContext.Provider value={value}>{children}</SupabaseContext.Provider>;
}

export function useSupabase() {
	return useContext(SupabaseContext);
}
